import AccountProfileImage from '@atoms/AccountProfileImage/AccountProfileImage';
import ReturnLink from '@atoms/ReturnLink/ReturnLink';
import type { IAccountHero } from '@models/IAccountHero';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

const AccountHero = ({
  title,
  returnLink,
  name,
  profileImageViewModel,
  initials,
  height = 'tall',
}: IAccountHero) => {
  const heightStyles = {
    tall: `${
      returnLink ? 'pb-12 pt-[1.125rem]' : 'py-9'
    } xl:pb-[4.375rem] xl:pt-[8.75rem]`,
    medium: 'py-7.5 xl:py-[4.375rem]',
    short: 'py-[0.9375rem] xl:py-7.5',
  };
  const isShort = height === 'short';
  const isTall = height === 'tall';
  const isMobile = useMediaQuery(768);

  const shortTitleStyles = isMobile ? 'body-one' : 'h4';

  return (
    <section
      className={classNames(
        'dark bg-black-100 text-white',
        heightStyles[height]
      )}
      data-testid="CT-04-Account-Hero"
      aria-labelledby="account-hero-title"
    >
      <div className="container flex flex-col gap-y-5">
        {returnLink?.name && returnLink?.url && (
          <div className="flex">
            <ReturnLink className="block" link={returnLink} />
          </div>
        )}

        <div className="flex items-center justify-between">
          <h1
            id="account-hero-title"
            className={classNames({
              'hidden xl:block': !returnLink && isTall,
              [shortTitleStyles]: isShort,
            })}
            data-testid="CT-04-Account-Hero-Title"
          >
            {title}
          </h1>

          <div
            className={classNames(
              returnLink || !isTall ? 'hidden xl:flex' : 'flex',
              'items-center gap-5'
            )}
            data-testid="CT-04-Account-Hero-Return-Link"
          >
            {name && (
              <div
                className={classNames(
                  { 'order-2 xl:order-none': !returnLink },
                  isShort ? 'body-three' : 'body-one'
                )}
                data-testid="CT-04-Account-Hero-Name"
              >
                {name}
              </div>
            )}

            {(profileImageViewModel?.imageData || initials) && (
              <AccountProfileImage
                image={profileImageViewModel}
                initials={initials}
                dataTestId="CT-04-Account-Hero-Account-Profile-Image"
                {...(isShort
                  ? {
                      sizeClassName: 'w-10 h-10',
                      initialsSize: 'body-three',
                    }
                  : {})}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccountHero;
